import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { registerSW } from 'virtual:pwa-register';

/**
 * Non-intrusive update prompt.
 *
 * The service worker is registered with `prompt` rather than `autoUpdate`
 * because reloading while someone is halfway through editing a class would
 * discard their input. The banner also confirms first-time offline readiness,
 * which is otherwise invisible.
 */
export function UpdatePrompt(): JSX.Element | null {
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [update, setUpdate] = useState<(() => Promise<void>) | null>(null);

  useEffect(() => {
    const updateSW = registerSW({
      onNeedRefresh: () => setNeedsRefresh(true),
      onOfflineReady: () => setOfflineReady(true),
    });
    setUpdate(() => async () => {
      await updateSW(true);
    });
  }, []);

  if (needsRefresh) {
    return (
      <div class="update-banner" role="status">
        <span>A new version is ready.</span>
        <button type="button" class="primary" onClick={() => void update?.()}>
          Reload
        </button>
        <button type="button" onClick={() => setNeedsRefresh(false)}>
          Later
        </button>
      </div>
    );
  }

  if (offlineReady) {
    return (
      <div class="update-banner" role="status">
        <span>Ready to work offline.</span>
        <button type="button" onClick={() => setOfflineReady(false)}>
          Dismiss
        </button>
      </div>
    );
  }

  return null;
}
